/**
 * "Put it on the home screen", on the capture page (QA-0014): the phone app is a web app, and
 * nothing in it said it can be installed. Chrome and Android offer their own prompt, which a button
 * opens; iOS has none, so it gets the two taps in Safari's share menu. Nothing once installed, on
 * a desktop, or after it was dismissed (remembered in this browser).
 */
import { Share, Smartphone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

const DISMISSED = "install-hint-dismissed";

type InstallPrompt = Event & { prompt: () => Promise<void> };

function installed(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

export function InstallHint() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [dismissed, setDismissed] = useState(wasDismissed);

  useEffect(() => {
    const keep = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", keep);
    return () => window.removeEventListener("beforeinstallprompt", keep);
  }, []);

  if (dismissed || installed() || (!prompt && !isIos())) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, "1");
    } catch {
      // Without storage the hint comes back next time.
    }
  };

  return (
    <div
      className="mt-3 flex items-start gap-3 rounded-lg border bg-card p-3 text-sm"
      data-testid="install-hint"
    >
      <Smartphone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-medium">{t("capture.install.title")}</p>
        {prompt ? (
          <Button
            size="sm"
            onClick={async () => {
              await prompt.prompt();
              setPrompt(null);
            }}
            data-testid="install-app"
          >
            {t("capture.install.button")}
          </Button>
        ) : (
          <p className="text-muted-foreground">
            <Share className="mr-1 inline size-3.5 align-text-bottom" />
            {t("capture.install.ios")}
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="-m-1 size-8"
        aria-label={t("capture.install.dismiss")}
        onClick={dismiss}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}
